import React, { ReactChild, ReactFragment, ReactPortal, useEffect } from 'react';
import ListSubheader from '@material-ui/core/ListSubheader';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import styled from 'styled-components';
import { OptionTypeWithCode } from '../types';

import { useResetCache } from './utils/useResetCache';

const GROUP_LABEL_HEIGHT = 48;
const OPTION_LABEL_HEIGHT = 41;
const OPTION_CODE_HEIGHT = 21;
const LISTBOX_PADDING = 8;
const SECONDARY_SUB_LABEL_HEIGHT = 20;

export interface ListboxComponentProps {
  /*! width of the dropdown menu */
  width?: string;
  children?: React.ReactNode;
  scrollSelectedItemIntoView?: boolean;
}

// Adapter for react-window
export const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>((props, ref) => {
  const { children, width, scrollSelectedItemIntoView, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;

  const gridRef = useResetCache(itemCount);

  useEffect(() => {
    // Scroll to selected item.
    if (scrollSelectedItemIntoView && props?.children && gridRef.current) {
      const childrenArray = React.Children.toArray(props.children);
      const index = childrenArray.findIndex((child) => React.isValidElement(child) && child.props['aria-selected']);
      if (index === -1) return;

      setTimeout(() => {
        const height = getHeight(itemCount, itemData);
        const padding = 2 * LISTBOX_PADDING;
        const listBoxMidPoint = (height - padding) / 2;
        const childSize = getChildSize(childrenArray[index]);
        const scrollOffset = childSize * (index + 1) - listBoxMidPoint;
        gridRef.current?.scrollTo(scrollOffset);
      }, 0); // setTimeout is needed to wait for the grid to render
    }
  }, [gridRef, props?.children, scrollSelectedItemIntoView, itemCount, itemData]);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight(itemCount, itemData) + 2 * LISTBOX_PADDING}
          width={width ?? '100%'}
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType={StyledListItemWrapper}
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const getChildSize = (child?: ReactChild | ReactFragment | ReactPortal) => {
  if (React.isValidElement(child) && child.type === ListSubheader) {
    return GROUP_LABEL_HEIGHT;
  }
  const item: { option?: OptionTypeWithCode } = (child as ReactPortal)?.props?.children?.props ?? {};
  return (
    OPTION_LABEL_HEIGHT +
    (item.option?.code || !!item.option?.chip || item.option?.subLabel ? OPTION_CODE_HEIGHT : 0) +
    (item.option?.secondarySubLabel ? SECONDARY_SUB_LABEL_HEIGHT : 0)
  );
};

const NUM_ROWS = 8;
const getHeight = (itemCount: number, itemData: Array<ReactChild | ReactFragment | ReactPortal>) => {
  if (itemCount > NUM_ROWS) {
    return itemData
      .slice(0, NUM_ROWS)
      .map(getChildSize)
      .reduce((a, b) => a + b, 0);
  }
  return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
};

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const StyledListItemWrapper = styled.ul`
  padding: 0;
  margin: 0;
`;
